import { useState } from 'react';
import { type Photo } from '@peles/shared';
import { usePhotoBatchUrls } from '../../hooks/usePhotos';
import {
  ImageOff,
  ZoomIn,
  X,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Square,
  Play,
  AlertCircle,
  Star,
  Globe,
} from 'lucide-react';

/**
 * The website section a photograph is filed under, or null when it carries no
 * category tag. Shown on the tile so a screenful of thumbnails says what is
 * filed where, instead of having to open each one.
 */
function categoryOf(photo: Photo): string | null {
  return photo.tags.find((tag) => tag.entityType === 'EVENT_TYPE')?.entityName ?? null;
}

export type PhotoGridSize = 'sm' | 'md' | 'lg';

interface PhotoGridProps {
  photos: Photo[];
  selectedPhotoId?: string | null;
  onSelectPhoto: (id: string) => void;
  isLoading?: boolean;
  size?: PhotoGridSize;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  multiSelectMode?: boolean;
}

function isVideo(photo: Photo): boolean {
  return photo.mimeType.startsWith('video/');
}

const SIZE_COLS: Record<PhotoGridSize, string> = {
  sm: 'grid-cols-3 md:grid-cols-4 lg:grid-cols-6',
  md: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
  lg: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
};

export function PhotoGrid({
  photos,
  selectedPhotoId,
  onSelectPhoto,
  isLoading,
  size = 'md',
  selectedIds,
  onToggleSelect,
  multiSelectMode = false,
}: PhotoGridProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [videoError, setVideoError] = useState(false);

  const photoIds = photos.map((p) => p.id);
  const { data: urlMap = {} } = usePhotoBatchUrls(photoIds);

  if (isLoading) {
    return <div className="text-gray-500 text-sm py-8 text-center">Loading photos...</div>;
  }

  if (photos.length === 0) {
    return <div className="text-gray-500 text-sm py-8 text-center">No photos found</div>;
  }

  const handleItemClick = (photo: Photo, index: number) => {
    if (multiSelectMode && onToggleSelect) {
      onToggleSelect(photo.id);
    } else {
      onSelectPhoto(photo.id);
      setLightboxIndex(index);
    }
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
    setVideoError(false);
  };

  const moveLightbox = (dir: -1 | 1) => {
    if (lightboxIndex === null) return;
    const next = lightboxIndex + dir;
    if (next >= 0 && next < photos.length) {
      setLightboxIndex(next);
      setVideoError(false);
    }
  };

  return (
    <>
      <div className={`grid ${SIZE_COLS[size]} gap-3`}>
        {photos.map((photo, index) => {
          const url = urlMap[photo.id];
          const isChecked = selectedIds?.has(photo.id) ?? false;
          const isSelected = selectedPhotoId === photo.id;

          return (
            <div
              key={photo.id}
              onClick={() => handleItemClick(photo, index)}
              className={`rounded-lg overflow-hidden cursor-pointer border-2 transition-all relative ${
                isSelected && !multiSelectMode
                  ? 'border-primary-500 ring-2 ring-primary-200'
                  : isChecked
                    ? 'border-primary-400 ring-2 ring-primary-100'
                    : 'border-transparent hover:border-gray-300'
              }`}
            >
              <div className="aspect-square bg-gray-100 relative group">
                {url ? (
                  isVideo(photo) ? (
                    <div className="w-full h-full relative bg-gray-900">
                      <video
                        src={url}
                        className="w-full h-full object-cover"
                        preload="metadata"
                        muted
                        playsInline
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <div className="w-12 h-12 bg-white/30 rounded-full flex items-center justify-center">
                          <Play className="text-white ml-0.5" size={24} fill="white" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={url}
                      alt={photo.caption || photo.fileName}
                      className="w-full h-full object-cover"
                    />
                  )
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageOff className="text-gray-300" size={32} />
                  </div>
                )}
                {/* Zoom hint on hover (non-multiselect, non-video) */}
                {!multiSelectMode && url && !isVideo(photo) && (
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <ZoomIn className="text-white drop-shadow" size={24} />
                  </div>
                )}
                {/* Checkbox overlay in multi-select */}
                {multiSelectMode && (
                  <div className="absolute top-1.5 left-1.5">
                    {isChecked ? (
                      <CheckSquare className="text-primary-500 drop-shadow" size={20} />
                    ) : (
                      <Square className="text-white drop-shadow" size={20} />
                    )}
                  </div>
                )}
                {/*
                  What is on the website, readable across a whole screen of
                  thumbnails. It was only ever visible by opening a photo one at
                  a time, which is no way to find out what four hundred of them
                  are doing. Featured implies public, so only the stronger of
                  the two is shown.
                */}
                {(photo.isPublic || photo.showOnLanding) && (
                  <div className="absolute top-1.5 right-1.5 flex flex-col items-end gap-1">
                    {photo.showOnLanding ? (
                      <span
                        title="Featured on the landing page"
                        className="inline-flex items-center gap-1 rounded-full bg-amber-500/95 px-2 py-0.5 text-[10px] font-semibold text-white shadow"
                      >
                        <Star size={10} fill="currentColor" />
                        Featured
                      </span>
                    ) : (
                      <span
                        title="Shown in the public gallery"
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-600/95 px-2 py-0.5 text-[10px] font-semibold text-white shadow"
                      >
                        <Globe size={10} />
                        Public
                      </span>
                    )}
                  </div>
                )}
              </div>
              {size !== 'sm' && (
                <div className="p-2">
                  <p className="text-xs text-gray-700 truncate">
                    {photo.caption || photo.fileName}
                  </p>
                  {categoryOf(photo) ? (
                    <p className="mt-0.5 text-[10px] font-medium text-primary-600">
                      {categoryOf(photo)}
                    </p>
                  ) : (
                    photo.tags.length > 0 && (
                      <p className="text-[10px] text-gray-400 mt-0.5">{photo.tags.length} tag(s)</p>
                    )
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={closeLightbox}
          >
            <X size={28} />
          </button>
          {lightboxIndex > 0 && (
            <button
              className="absolute left-4 text-white hover:text-gray-300 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                moveLightbox(-1);
              }}
            >
              <ChevronLeft size={40} />
            </button>
          )}
          {lightboxIndex < photos.length - 1 && (
            <button
              className="absolute right-4 text-white hover:text-gray-300 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                moveLightbox(1);
              }}
            >
              <ChevronRight size={40} />
            </button>
          )}
          <div
            className="max-w-5xl max-h-[90vh] flex flex-col items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            {urlMap[photos[lightboxIndex].id] ? (
              isVideo(photos[lightboxIndex]) ? (
                videoError ? (
                  <div className="flex flex-col items-center gap-3 text-white p-8">
                    <AlertCircle size={48} className="text-yellow-400" />
                    <p className="text-sm text-center max-w-xs">
                      This video format cannot be played in your browser. Try downloading the file
                      to play it locally.
                    </p>
                    <a
                      href={urlMap[photos[lightboxIndex].id]}
                      download={photos[lightboxIndex].fileName}
                      className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Download video
                    </a>
                  </div>
                ) : (
                  <video
                    key={photos[lightboxIndex].id}
                    src={urlMap[photos[lightboxIndex].id]}
                    controls
                    className="max-h-[80vh] max-w-full rounded-lg"
                    onError={() => setVideoError(true)}
                  />
                )
              ) : (
                <img
                  src={urlMap[photos[lightboxIndex].id]}
                  alt={photos[lightboxIndex].caption || photos[lightboxIndex].fileName}
                  className="max-h-[80vh] max-w-full object-contain rounded-lg"
                />
              )
            ) : (
              <div className="w-64 h-64 flex items-center justify-center">
                <ImageOff className="text-gray-500" size={48} />
              </div>
            )}
            {photos[lightboxIndex].caption && (
              <p className="text-white text-sm">{photos[lightboxIndex].caption}</p>
            )}
            <p className="text-gray-400 text-xs">
              {lightboxIndex + 1} / {photos.length}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
